import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { logout } from '../../features/auth/logout'
import apiRequest from '../../lib/http'
import { ACTIVE_COMPANY_KEY, clearAmbientSelection } from '../../lib/ambientHeaders'

// Multi-brand. Shown when the server refuses a request because the caller holds more than one firm
// and named none, and reachable from the switcher. It asks /company/mine rather than /company: the
// latter returns every firm to any staff member, which with two firms discloses the other one.
const CompanySelector = () => {
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await apiRequest.get('/company/mine')
        const data = res.data.data || []
        setCompanies(data)

        // One firm is every caller today, and asking them to choose between one thing is not a
        // choice. Store it anyway so the header is explicit rather than inferred.
        if (data.length <= 1) {
          if (data.length === 1) {
            localStorage.setItem(ACTIVE_COMPANY_KEY, data[0]._id)
          }
          navigate('/', { replace: true })
        }
      } catch (err) {
        // A blank page is the worst possible outcome here: this screen is where a caller lands when
        // every other request is being refused, so it is their only way out. Say what happened and
        // offer the one action that always works.
        console.error('Failed to fetch companies:', err)
        setFailed(true)
      } finally {
        setLoading(false)
      }
    }
    fetchCompanies()
  }, [])

  const handleSelect = (companyId) => {
    // The office belongs to the firm being left. It lives in localStorage, so it is the one piece of
    // firm-scoped state a full reload does NOT clear — the next request would carry firm B's brand
    // and firm A's office together.
    clearAmbientSelection()
    localStorage.setItem(ACTIVE_COMPANY_KEY, companyId)
    // A full reload rather than a route change: every cached list in the store belongs to the firm
    // that was active when it was fetched, and a switch that left them in place would show one
    // firm's contacts under the other firm's name.
    window.location.assign('/')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="text-text-secondary">Loading...</div>
      </div>
    )
  }

  if (failed) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="border border-divider/20 shadow-lg rounded-2xl p-12 max-w-md w-full">
          <h1 className="text-2xl font-bold text-text-primary mb-2">Could not load your brands</h1>
          <p className="text-text-secondary mb-8">
            We could not reach the server to find out which firms you can work in. Sign in again, and
            if it keeps happening tell support which account you are using.
          </p>
          <button
            onClick={() => logout()}
            className="w-full p-4 rounded-xl border border-divider/20 hover:border-primary hover:bg-primary/5 transition-colors font-semibold text-text-primary"
          >
            Sign in again
          </button>
        </div>
      </div>
    )
  }

  if (companies.length <= 1) {
    return null
  }

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center">
      <div className="border border-divider/20 shadow-lg rounded-2xl p-12 max-w-md w-full">
        <h1 className="text-2xl font-bold text-text-primary mb-2">Select Brand</h1>
        <p className="text-text-secondary mb-8">
          Choose which firm you are working in. Contacts, payments, documents and everything you send
          on their behalf follow this choice.
        </p>
        <div className="space-y-3">
          {companies.map((company) => (
            <button
              key={company._id}
              onClick={() => handleSelect(company._id)}
              className="w-full text-left p-4 rounded-xl border border-divider/20 hover:border-primary hover:bg-primary/5 transition-colors"
            >
              {/* A firm with no name still has to be clickable and distinguishable. Rendering the
                  raw title gave an empty button — the operator sees a box with nothing in it and has
                  to guess which firm it is. The placeholder says what is actually wrong. */}
              <div className="font-semibold text-text-primary">{company.title || 'Unnamed firm'}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

export default CompanySelector
